<template>
  <li class="tree-node">
    <div
      :class="[
        'tree-node__row',
        `tree-node__row--${props.item.type || 'default'}`,
        { 'tree-node__row--selected': isSelected }
      ]"
    >
      <button
        v-if="isFolder"
        type="button"
        class="tree-node__toggle"
        @click.stop="toggle"
        aria-label="Ouvrir ou fermer la branche"
      >
        <span :class="['tree-node__chevron', { 'tree-node__chevron--open': isOpen }]"></span>
      </button>
      <span v-else class="tree-node__toggle tree-node__toggle--spacer"></span>

      <button
        type="button"
        class="tree-node__content"
        @click="select"
        @dblclick="handleDoubleClick"
      >
        <span class="tree-node__text">
          <span class="tree-node__name">{{ props.item.name }}</span>
        </span>
      </button>
    </div>

    <ul v-if="isFolder && isOpen" class="tree-node__children">
      <tree-item
        v-for="(child, index) in props.item.children"
        :key="`${child.id ?? child.name}-${index}`"
        class="item"
        :item="child"
        :default-open="props.defaultOpen"
        @select="selectPropagation"
      />
    </ul>
  </li>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import store from "@/store";

const props = defineProps({
  item: Object,
  defaultOpen: {
    type: Boolean,
    default: false
  }
});

const isOpen = ref(props.item?.type === "home" || props.defaultOpen);
const emit = defineEmits(["select"]);

const isFolder = computed(() => Array.isArray(props.item?.children) && props.item.children.length > 0);
const isSelected = computed(() => store.state.context.selectedTreeId === props.item.id);

const select = () => {
  emit("select", props.item);
};

const selectPropagation = (item) => {
  emit("select", item);
};

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const handleDoubleClick = () => {
  if (isFolder.value) {
    toggle();
  }
};

watch(
  () => props.defaultOpen,
  (value) => {
    if (value) {
      isOpen.value = true;
    }
  }
);
</script>

<style scoped>
.tree-node {
  list-style: none;
}

.tree-node__row {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 0;
}

.tree-node__row--selected .tree-node__content {
  background: #eff6ff;
  border-bottom-color: #bfdbfe;
  box-shadow: inset 3px 0 0 #2563eb;
}

.tree-node__toggle {
  flex: 0 0 1.9rem;
  width: 1.9rem;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tree-node__toggle--spacer {
  cursor: default;
}

.tree-node__chevron {
  width: 0.55rem;
  height: 0.55rem;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg);
  transition: transform 0.18s ease;
}

.tree-node__chevron--open {
  transform: rotate(45deg);
}

.tree-node__content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0.72rem 0.9rem 0.72rem 0.25rem;
  border: 0;
  border-bottom: 1px solid #eef2f7;
  border-radius: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.tree-node__content:hover {
  background: #f8fafc;
}

.tree-node__text {
  min-width: 0;
  display: block;
}

.tree-node__name {
  color: #111827;
  font-size: 0.93rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-node__children {
  margin: 0;
  padding: 0 0 0 0.9rem;
  border-left: 1px solid #e5e7eb;
}
</style>
